import { useRef } from "react";
import {
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from "framer-motion";
import { IMG } from "@/lib/site";
import { EASE, EASE_EXPO } from "@/lib/motion";
import { WhatsAppButton } from "./WhatsAppButton";
import greenDot from "@/assets/greendot.png";


const line = {
  initial: { y: "110%" },
  animate: (i) => ({
    y: "0%",
    transition: {
      duration: 0.38,
      ease: EASE_EXPO,
      delay: 0.12 + i * 0.04,
    },
  }),
};

const LineMask = ({ children, i = 0 }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      variants={line}
      initial="initial"
      animate="animate"
      custom={i}
      className="block will-change-transform"
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -220]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const rot = useTransform(scrollYProgress, [0, 1], [0, -6]);

  const parallax1 = useMotionTemplate`translate3d(0, ${y1}px, 0)`;
  const parallax2 = useMotionTemplate`translate3d(0, ${y2}px, 0) rotate(${rot}deg)`;
  const parallax3 = useMotionTemplate`translate3d(0, ${y3}px, 0)`;

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative pt-24 md:pt-32 pb-12 md:pb-24 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        {/* Meta row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
          className="flex flex-wrap items-center justify-between gap-2 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.28em] text-[#3B2818]/70 pb-5 md:pb-12 border-b border-[#3B2818]/15"
        >
          <span>Est. Homemade — Pune & Karad</span>
          <span className="hidden md:inline">A Small Kitchen, A Big Heart</span>
          <span>Baked to Order</span>
        </motion.div>

        <div className="mt-8 md:mt-14 grid grid-cols-12 gap-6 lg:gap-8 items-center">
          {/* Headline */}
          <div className="col-span-12 lg:col-span-7 relative z-10">
            <motion.span
              initial={{ opacity: 0, y: 10, rotate: -6 }}
              animate={{ opacity: 1, y: 0, rotate: -3 }}
              transition={{ duration: 0.5, delay: 0.15, ease: EASE }}
              className="font-script text-2xl sm:text-3xl md:text-5xl text-[#C06A22] inline-block mb-2 md:mb-4"
              data-testid="hero-script-accent"
            >
              Made with love,
            </motion.span>

            <h1
              data-testid="hero-headline"
              className="font-serif text-[38px] sm:text-[68px] md:text-[96px] lg:text-[112px] leading-[0.92] sm:leading-[0.88] tracking-[-0.02em] text-[#3B2818]"
            >
              <LineMask i={0}>From Our</LineMask>
              <LineMask i={1}>
                <span className="italic font-light">Kitchen</span>
              </LineMask>
              <LineMask i={2}>to your plate</LineMask>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5, ease: EASE }}
              className="mt-6 md:mt-10 max-w-lg text-sm sm:text-base md:text-lg leading-relaxed text-[#6B5343]"
              data-testid="hero-subtext"
            >
              Cakes, cookies, tea-time treats, cookie tins, premixes made with fresh ingredients
              and zero preservatives by a mother. Made fresh to order for families, kids, and celebrations.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6, ease: EASE }}
              className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3 sm:gap-4"
            >
              <WhatsAppButton
                size="lg"
                variant="primary"
                label="Order on WhatsApp"
                testId="hero-wa-cta"
              />
              <a
                href="#categories"
                data-testid="hero-menu-link"
                className="text-xs sm:text-sm uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[#3B2818]/80 hover:text-[#C06A22] transition-colors duration-200 underline-offset-8 hover:underline py-2"
              >
                See our fresh menu ↓
              </a>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7, ease: EASE }}
              className="mt-6 flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] sm:text-xs font-medium text-[#3B2818]/80"
            >
              <span className="flex items-center gap-1.5 bg-[#C06A22]/10 text-[#C06A22] px-3 py-1 rounded-full border border-[#C06A22]/20 shrink-0">
                Pure Vegetarian <img src={greenDot} alt="Pure Veg" className="w-4 h-4 object-contain" />
              </span>
              <span className="flex items-center gap-1.5 bg-[#C06A22]/10 text-[#C06A22] px-3 py-1 rounded-full border border-[#C06A22]/20 shrink-0">
                FSSAI Registered
              </span>
              <span className="flex items-center gap-1.5 bg-[#3B2818]/5 text-[#3B2818] px-3 py-1 rounded-full border border-[#3B2818]/15 shrink-0">
                ✓ Preservative Free
              </span>
              <span className="flex items-center gap-1.5 bg-[#3B2818]/5 text-[#3B2818] px-3 py-1 rounded-full border border-[#3B2818]/15 shrink-0">
                ✓ Doorstep Delivery
              </span>
            </motion.div>
          </div>

          {/* Desktop Parallax Mosaic (md and up) */}
          <div className="hidden md:block col-span-12 lg:col-span-5 relative min-h-[500px] lg:min-h-[640px]">
            <motion.div
              style={{ transform: parallax1 }}
              className="absolute top-0 right-0 w-[62%] aspect-[3/4]"
            >
              <motion.div
                initial={{ opacity: 0, y: 40, scale: 1.02 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.45, delay: 0.2, ease: EASE_EXPO }}
                className="relative w-full h-full overflow-hidden rounded-[24px] shadow-[0_30px_60px_-20px_rgba(59,40,24,0.35)] grain transform-gpu will-change-transform [backface-visibility:hidden]"
                data-testid="hero-image-primary"
              >
                <img
                  src={IMG.hero.a}
                  srcSet={`${IMG.hero.aSm} 800w, ${IMG.hero.a} 1400w`}
                  sizes="(max-width: 768px) 100vw, 45vw"
                  alt="A fresh homemade cake"
                  width="1400"
                  height="1866"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </motion.div>
            </motion.div>

            <motion.div
              style={{ transform: parallax2 }}
              className="absolute top-[36%] left-0 w-[52%] aspect-square"
            >
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.28, ease: EASE_EXPO }}
                className="relative w-full h-full overflow-hidden rounded-[24px] border border-[#3B2818]/15 grain transform-gpu will-change-transform [backface-visibility:hidden]"
                data-testid="hero-image-secondary"
              >
                <img
                  src={IMG.hero.b}
                  srcSet={`${IMG.hero.bSm} 600w, ${IMG.hero.b} 1200w`}
                  sizes="(max-width: 768px) 50vw, 30vw"
                  alt="Warm bakes close up"
                  width="1200"
                  height="1200"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            </motion.div>

            <motion.div
              style={{ transform: parallax3 }}
              className="absolute bottom-0 right-[10%] w-[44%] aspect-[4/5]"
            >
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.35, ease: EASE_EXPO }}
                className="relative w-full h-full overflow-hidden rounded-[24px] grain shadow-[0_20px_40px_-15px_rgba(59,40,24,0.35)] transform-gpu will-change-transform [backface-visibility:hidden]"
                data-testid="hero-image-tertiary"
              >
                <img
                  src={IMG.hero.c}
                  srcSet={`${IMG.hero.cSm} 600w, ${IMG.hero.c} 1200w`}
                  sizes="(max-width: 768px) 50vw, 25vw"
                  alt="A batch of cookies"
                  width="1200"
                  height="1500"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, delay: 0.45, ease: EASE_EXPO }}
              className="absolute -bottom-2 left-[6%] bg-[#3B2818] text-[#FAF6F0] rounded-full px-5 py-3 shadow-lg flex items-center gap-3 z-20"
              data-testid="hero-floating-badge"
            >
              <span className="h-2 w-2 rounded-full bg-[#25D366] animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.22em]">
                Fresh batch — today
              </span>
            </motion.div>
          </div>

          {/* Mobile Structured Gallery (under md breakpoint) */}
          <div className="block md:hidden col-span-12 mt-6 relative">
            <div className="grid grid-cols-2 gap-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="col-span-2 relative aspect-[16/10] overflow-hidden rounded-[20px] shadow-md border border-[#3B2818]/10"
              >
                <img
                  src={IMG.hero.a}
                  srcSet={`${IMG.hero.aSm} 800w, ${IMG.hero.a} 1400w`}
                  sizes="100vw"
                  alt="Fresh homemade cake"
                  width="1400"
                  height="875"
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                />
                <div className="absolute bottom-3 left-3 bg-[#3B2818]/90 backdrop-blur-md text-[#FAF6F0] rounded-full px-3.5 py-1.5 shadow-md flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#25D366] animate-pulse" />
                  <span className="text-[10px] uppercase tracking-[0.18em]">
                    Fresh batch — today
                  </span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="relative aspect-[4/3] overflow-hidden rounded-[16px] border border-[#3B2818]/10"
              >
                <img
                  src={IMG.hero.b}
                  srcSet={`${IMG.hero.bSm} 600w, ${IMG.hero.b} 1200w`}
                  sizes="50vw"
                  alt="Warm bakes close up"
                  width="600"
                  height="450"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 }}
                className="relative aspect-[4/3] overflow-hidden rounded-[16px] border border-[#3B2818]/10"
              >
                <img
                  src={IMG.hero.c}
                  srcSet={`${IMG.hero.cSm} 600w, ${IMG.hero.c} 1200w`}
                  sizes="50vw"
                  alt="A batch of cookies"
                  width="600"
                  height="450"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
